// constants/theme.ts
// ─── Editorial design system for Kursi (non-KURSI-LEARN surfaces) ────────────
// Aesthetic direction: "The Career Editorial"
//   • Warm cream paper, rich ink, single confident terracotta accent
//   • Hebrew serif display (Frank Ruhl Libre) + Heebo sans body + Space Mono meta
//   • Heavy editorial rules, oversized numerals, asymmetric grids
// KURSI LEARN keeps its own theming via constants/kursiPlayThemes.ts
// ─────────────────────────────────────────────────────────────────────────────

import { Platform } from 'react-native';

// ─── Brand legacy violets (preserved for KURSI LEARN imports only) ──────────
const BRAND_VIOLET       = '#7C3AED';
const BRAND_VIOLET_LIGHT = '#A78BFA';
const BRAND_VIOLET_DARK  = '#5B21B6';
const BRAND_GLOW         = 'rgba(124, 58, 237, 0.30)';
const BRAND_PURPLE       = BRAND_VIOLET;
const BRAND_PURPLE_LIGHT = BRAND_VIOLET_LIGHT;
const BRAND_PURPLE_DARK  = BRAND_VIOLET_DARK;

// ─── SaaS product palette ────────────────────────────────────────────────────
//   Paper:    clean off-white, slightly cool — modern SaaS feel
//   Ink:      rich near-black
//   Accent:   premium purple — the single confident brand color
//   Crimson:  reserved for errors
//   Forest:   reserved for success states
//   Gold:     reserved for "premium" / featured moments
const PAPER          = '#F8F7FC'; // primary background, cool off-white
const PAPER_DEEP     = '#EEE9F8'; // slightly deeper surface
const PAPER_SOFT     = '#FFFFFF'; // cards, elevated surfaces
const INK            = '#1A1625'; // primary text, near-black with purple tint
const INK_SOFT       = '#3A3350'; // secondary text
const INK_MUTED      = '#7A6FA0'; // tertiary text / captions
const INK_FAINT      = '#C0BAD8'; // muted dividers, disabled states
const RULE           = '#1A1625'; // hairlines, always ink
const ACCENT         = '#7C3AED'; // premium purple — the one accent color
const ACCENT_SOFT    = '#EDE9FC'; // tinted accent surface
const ACCENT_DEEP    = '#5B21B6'; // hovered/pressed accent
const CRIMSON        = '#A8253A'; // error red
const FOREST         = '#3C5E3B'; // success green
const GOLD           = '#B8893B'; // premium gold

// ─── Shadows ─────────────────────────────────────────────────────────────────
// Editorial design uses rules and weight, not drop shadows.
// Shadows are kept minimal — only when something needs to physically lift.
const shadows = {
  sm: Platform.select({
    web: { boxShadow: '0 1px 0 rgba(26,22,17,0.10)' },
    default: { shadowColor: '#1A1611', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.10, shadowRadius: 0, elevation: 1 },
  }) as any,
  md: Platform.select({
    web: { boxShadow: '0 2px 0 rgba(26,22,17,0.12), 0 12px 24px rgba(26,22,17,0.06)' },
    default: { shadowColor: '#1A1611', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.12, shadowRadius: 8, elevation: 3 },
  }) as any,
  lg: Platform.select({
    web: { boxShadow: '0 4px 0 rgba(26,22,17,0.14), 0 24px 48px rgba(26,22,17,0.10)' },
    default: { shadowColor: '#1A1611', shadowOffset: { width: 0, height: 6 }, shadowOpacity: 0.16, shadowRadius: 18, elevation: 6 },
  }) as any,
};

// ─── Base tokens ────────────────────────────────────────────────────────────
const base = {
  spacing:      { xs: 4, sm: 8, md: 16, lg: 24, xl: 40, xxl: 64, mega: 96 },
  borderRadius: { sm: 2, md: 3, lg: 4, xl: 6, full: 9999 }, // sharp editorial corners
  interaction: {
    minTouchTarget: 44,
    transitionFast: 160,
    transitionBase: 240,
    transitionSlow: 480,
  },
  fonts: {
    // Primary sans — Noto Sans Hebrew
    regular:  'NotoSansHebrew-Regular',
    medium:   'NotoSansHebrew-Medium',
    semiBold: 'NotoSansHebrew-SemiBold',
    bold:     'NotoSansHebrew-Bold',
    // Display headings — Noto Sans Hebrew ExtraBold / Black
    serif:        'NotoSansHebrew-Regular',
    serifMedium:  'NotoSansHebrew-Medium',
    serifBold:    'NotoSansHebrew-Bold',
    serifBlack:   'NotoSansHebrew-ExtraBold',
    // Meta / mono — kept for labels and kickers
    mono:         'NotoSansHebrew-Medium',
  },
  // Type scale — editorial: oversized headlines, generous body
  type: {
    display:   Platform.OS === 'web' ? 96 : 56,
    headline:  Platform.OS === 'web' ? 64 : 40,
    title:     Platform.OS === 'web' ? 40 : 28,
    section:   Platform.OS === 'web' ? 28 : 22,
    body:      17,
    bodyLg:    19,
    small:     14,
    caption:   12,
    kicker:    11, // uppercase mono kicker label
  },
  shadows,
};

// ─── Editorial light (default for all non-KURSI-LEARN screens) ──────────────
const editorial = {
  mode: 'editorial' as const,
  colors: {
    // Editorial canonical names
    paper:        PAPER,
    paperSoft:    PAPER_SOFT,
    paperDeep:    PAPER_DEEP,
    ink:          INK,
    inkSoft:      INK_SOFT,
    inkMuted:     INK_MUTED,
    inkFaint:     INK_FAINT,
    rule:         RULE,
    accent:       ACCENT,
    accentSoft:   ACCENT_SOFT,
    accentDeep:   ACCENT_DEEP,
    crimson:      CRIMSON,
    forest:       FOREST,
    gold:         GOLD,

    // ─── Legacy aliases (so existing screens read sensible colors) ──────────
    primary:         ACCENT,
    primaryLight:    ACCENT_SOFT,
    primaryDark:     ACCENT_DEEP,
    secondary:       PAPER_DEEP,
    background:      PAPER,
    backgroundLight: PAPER_SOFT,
    surface:         PAPER_SOFT,
    cardBackground:  PAPER_SOFT,
    text:            INK,
    textLight:       INK_SOFT,
    textMuted:       INK_MUTED,
    border:          INK,
    borderGlow:      'transparent',
    outlineLight:    INK,
    outlineDark:     INK,
    shadow:          'rgba(26,22,17,0.10)',
    success:         FOREST,
    error:           CRIMSON,
  },
};

// ─── Dark (for chat/learn-adjacent dark surfaces if needed) ─────────────────
const editorialDark = {
  mode: 'editorial-dark' as const,
  colors: {
    paper:        '#0D0B1A',
    paperSoft:    '#16132A',
    paperDeep:    '#09071A',
    ink:          '#F0EEF8',
    inkSoft:      '#C4BBDC',
    inkMuted:     '#8A7FAA',
    inkFaint:     '#4A4468',
    rule:         '#F0EEF8',
    accent:       '#A78BFA',
    accentSoft:   '#2D1A5E',
    accentDeep:   '#C4B5FD',
    crimson:      '#E25C72',
    forest:       '#7BAE7A',
    gold:         '#E8B968',

    primary:         '#A78BFA',
    primaryLight:    '#2D1A5E',
    primaryDark:     '#C4B5FD',
    secondary:       '#16132A',
    background:      '#0D0B1A',
    backgroundLight: '#16132A',
    surface:         '#16132A',
    cardBackground:  '#16132A',
    text:            '#F0EEF8',
    textLight:       '#C4BBDC',
    textMuted:       '#8A7FAA',
    border:          '#F0EEF8',
    borderGlow:      'transparent',
    outlineLight:    '#F0EEF8',
    outlineDark:     '#F0EEF8',
    shadow:          'rgba(0,0,0,0.40)',
    success:         '#7BAE7A',
    error:           '#E25C72',
  },
};

// ─── Back-compat dark/light slots (KURSI LEARN still imports these names) ───
// These map onto the editorial palettes so cross-product imports keep compiling.
const dark = editorialDark;
const light = editorial;

export const themes = {
  editorial:     { ...base, ...editorial },
  editorialDark: { ...base, ...editorialDark },
  // Legacy keys
  dark:          { ...base, ...dark },
  light:         { ...base, ...light },
} as const;

// Default theme used across all non-KURSI-LEARN screens.
export const theme = themes.editorial;

export {
  BRAND_VIOLET, BRAND_VIOLET_LIGHT, BRAND_VIOLET_DARK, BRAND_GLOW,
  BRAND_PURPLE, BRAND_PURPLE_LIGHT, BRAND_PURPLE_DARK,
  PAPER, PAPER_SOFT, PAPER_DEEP,
  INK, INK_SOFT, INK_MUTED, INK_FAINT, RULE,
  ACCENT, ACCENT_SOFT, ACCENT_DEEP,
  CRIMSON, FOREST, GOLD,
};
